import { Box, Switch, TextField, Tooltip, Typography } from "@mui/material";
import { Popover } from '@base-ui-components/react/popover';
import AddIcon from '@mui/icons-material/Add';
import React, { useRef, useState } from "react";
import DefaultButton from "../general/buttons/DefaultButton";
import { createPlaylist } from "../../services/playlist";
import Playlist from "../../models/Playlist";
import { colors } from "../../theme";
import { popupStyle, positionerStyle, closeButtonStyle, backdropStyle } from "../general/buttons/triggerStyles";

interface CreatePlaylistButtonProps {
    onCreated: (playlist: Playlist) => void;
}

// A plain styled element rather than an IconButton inside Popover.Trigger -
// base-ui's trigger is already a <button>, and nesting a MUI IconButton's own
// <button> inside it is invalid HTML that React flags as a hydration error
// (see triggerStyles.ts).
export default function CreatePlaylistButton({ onCreated }: CreatePlaylistButtonProps) {
    const [name, setName] = useState("");
    const [isPublic, setIsPublic] = useState(false);
    const [creating, setCreating] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const closeButtonRef = useRef<HTMLButtonElement>(null);
    const nameInputRef = useRef<HTMLInputElement>(null);

    const reset = () => {
        setName("");
        setIsPublic(false);
        setError(null);
        setCreating(false);
    };

    const handleCreate = async () => {
        const trimmed = name.trim();
        if (!trimmed) {
            setError("Give it a name first.");
            return;
        }
        setCreating(true);
        setError(null);
        try {
            const playlist = await createPlaylist(trimmed, undefined, isPublic);
            onCreated(playlist);
            closeButtonRef.current?.click();
            reset();
        } catch (err: any) {
            setError(err?.message || "Failed to create playlist");
            setCreating(false);
        }
    };

    return (
        <Popover.Root
            onOpenChange={(open) => {
                if (open) {
                    setTimeout(() => nameInputRef.current?.focus(), 50);
                } else {
                    reset();
                }
            }}
        >
            <Tooltip title="Create playlist" placement="top">
                <Popover.Trigger
                    aria-label="Create playlist"
                    style={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 36,
                        height: 36,
                        borderRadius: "50%",
                        border: "none",
                        background: "none",
                        padding: 0,
                        color: colors.textSecondary,
                        cursor: "pointer",
                        transition: "color 120ms ease, transform 120ms ease, background-color 120ms ease",
                    }}
                >
                    <AddIcon sx={{ fontSize: 24 }} />
                </Popover.Trigger>
            </Tooltip>

            <Popover.Portal>
                <Popover.Backdrop style={backdropStyle} />
                <Popover.Positioner style={positionerStyle}>
                    <Popover.Popup style={popupStyle}>
                        <Popover.Close ref={closeButtonRef} aria-label="Close" style={closeButtonStyle}>
                            ✕
                        </Popover.Close>

                        <Typography variant="h5" sx={{ mb: 0.5 }}>New playlist</Typography>
                        <Typography variant="body2" sx={{ mb: 2.5 }}>
                            Name it, then invite people to start adding songs.
                        </Typography>

                        <TextField
                            inputRef={nameInputRef}
                            label="Playlist name"
                            fullWidth
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                    e.preventDefault();
                                    handleCreate();
                                }
                            }}
                            sx={{ mb: 1.5 }}
                        />

                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 2,
                                mb: 1.5,
                            }}
                        >
                            <Box sx={{ minWidth: 0 }}>
                                <Typography sx={{ fontSize: 14, fontWeight: 600 }}>Public</Typography>
                                <Typography sx={{ fontSize: 12, color: colors.textMuted }}>
                                    {isPublic
                                        ? "Anyone can find it on your profile and follow it."
                                        : "Hidden from your profile - share it with an invite link instead."}
                                </Typography>
                            </Box>
                            <Switch
                                checked={isPublic}
                                onChange={(e) => setIsPublic(e.target.checked)}
                                inputProps={{ "aria-label": "Public playlist" }}
                            />
                        </Box>

                        {error && (
                            <Typography sx={{ color: colors.danger, fontSize: 13, mb: 1.5 }}>
                                {error}
                            </Typography>
                        )}

                        <Box sx={{ mt: 2 }}>
                            <DefaultButton
                                text={creating ? "Creating…" : "Create playlist"}
                                onClick={handleCreate}
                                disabled={creating}
                                fullWidth
                            />
                        </Box>
                    </Popover.Popup>
                </Popover.Positioner>
            </Popover.Portal>
        </Popover.Root>
    );
}
